/* DAL BOT AL BACKTEST — la precompilazione, e ciò che di essa non è deducibile.
 *
 * È il percorso gemello di `creazione.ts`, percorso al contrario: là si parte da
 * una misura per accendere un bot, qui si parte da un bot acceso per rifarne la
 * misura su un periodo scelto. La posta è la stessa e va detta: un backtest
 * precompilato male non dà errore, dà un ALTRO risultato — e quel risultato
 * viene letto per decidere se lasciare acceso un bot che muove denaro vero.
 *
 * Le tre cose che questo file esiste per non sbagliare:
 *
 *  1. LE CANDELE NON SONO QUELLE DEL BOT. Il bot scambia `bot.asset/bot.currency`
 *     — su catena può essere `BTCB/USDT` — ma DECIDE sulle candele del suo market
 *     watcher (`tradebot.robot.ts`: `fetchDataFromOHLCV(marketWatcher.exchange,
 *     { asset: marketWatcher.asset, currency: marketWatcher.currency })`).
 *     Precompilare il backtest con la coppia scambiata significherebbe misurare
 *     un mercato su cui il bot non ha mai deciso — e su Binance `BTCB/USDT` non
 *     esiste nemmeno, quindi il backtest tornerebbe vuoto senza spiegare perché.
 *
 *  2. LO SLIPPAGE DEL BOT NON È UN COSTO. `swapSlippage` è la TOLLERANZA massima
 *     accettata a uno swap: il tetto oltre il quale la transazione viene
 *     rifiutata, non il prezzo pagato. Metterlo nel campo costi del backtest
 *     misurerebbe una strategia che paga lo 0,5% a lato quando ne paga lo 0,02.
 *     I costi veri, per venue, stanno misurati in `backtest/costi.ts`.
 *
 *  3. QUELLO CHE NON SI SA NON SI INVENTA — MA SI PUÒ SEMPRE SCEGLIERE. Ogni
 *     campo porta con sé da dove viene, e i quattro gradi di certezza sono
 *     distinti: `certo` (letto dalla riga), `scelto` (l'ha indicato chi guarda,
 *     e questo va detto accanto al risultato), `ripiego` (un valore che regge ma
 *     non è quello del bot) e `ignoto` (non c'è, e nessun valore plausibile lo
 *     sostituisce). La schermata mostra tutto ciò che non è `certo`.
 *
 *     E ACCANTO A OGNI DICHIARAZIONE CI DEVE ESSERE IL CONTROLLO PER RIMEDIARE.
 *     Una schermata che scrive «sceglilo a mano» e poi non offre con cosa è un
 *     vicolo cieco, cioè peggio di un valore indovinato: da un valore indovinato
 *     si può almeno proseguire e correggere: da un vicolo cieco no. Per exchange
 *     e coppia il rimedio è `GET /tradebots/market-watchers`, che è esattamente
 *     l'insieme delle sorgenti di candele possibili, più la scrittura a mano.
 *
 * ## Perché il market watcher può mancare
 *
 * Per mesi non è mai arrivato: `GET /tradebots/:id` è una rotta `@Crud` e le sue
 * `join` elencavano `account`, `account.exchange`, `trades` e `wallet` — non
 * `marketWatcher`. La relazione è `eager` sull'entity, ma nestjsx/crud costruisce
 * la query dalle proprie `join`, quindi il campo non usciva mai e il bot 12
 * sembrava senza sorgente di candele mentre aveva `marketWatcherId = 1`, cioè
 * BTC/USDT su Binance. Ora le join ci sono. Il ripiego resta perché una risposta
 * vecchia in cache, o una rotta che tornasse a dimenticarsene, non devono
 * riportare la schermata nel vicolo cieco.
 */

import type { MarketWatcher, RangeDatiStorici, Tradebot } from '../../api';
import { giorniDiWarmup } from '../../lib/warmup';
import { num } from '../../lib/formato';
import { VENUE, type Venue } from '../backtest/costi';
import type { CostiEsecuzione } from '../backtest/riesecuzione';
import { ETICHETTE_DEX } from './creazione';

/* ==========================================================================
 * Un valore, e da dove viene
 * ======================================================================== */

export type Certezza =
  /** Letto dalla riga del bot: è esattamente ciò con cui opera. */
  | 'certo'
  /** L'ha indicato chi guarda, perché la riga non lo diceva. Vale, ma va detto. */
  | 'scelto'
  /** Non c'è sulla riga: questo valore regge, ma non è quello del bot. */
  | 'ripiego'
  /** Non c'è e non si sostituisce: c'è il controllo per sceglierlo, lì accanto. */
  | 'ignoto';

export interface Dichiarato<T> {
  valore: T | null;
  certezza: Certezza;
  /** Da dove viene, o perché manca. Va scritto a schermo, non solo pensato. */
  fonte: string;
}

const CERTO = <T,>(valore: T, fonte: string): Dichiarato<T> => ({
  valore,
  certezza: 'certo',
  fonte,
});

/* ==========================================================================
 * Il mercato su cui il bot decide
 * ======================================================================== */

export interface MercatoDelBot {
  exchangeId: Dichiarato<number>;
  asset: Dichiarato<string>;
  currency: Dichiarato<string>;
  /**
   * La coppia scambiata è diversa da quella osservata: succede su catena, dove
   * il bot compra `BTCB` guardando le candele `BTC/USDT` di un exchange con
   * book. Non è un errore, ma chi legge il risultato deve saperlo.
   */
  coppiaScambiataDiversa: string | null;
}

/**
 * Exchange e coppia con cui rifare la misura.
 *
 * La fonte giusta è il MARKET WATCHER, non il bot: è la sorgente delle candele
 * su cui il bot prende ogni decisione. Il market watcher arriva eager con la
 * riga; quando non c'è si ripiega sul conto, dichiarandolo, perché l'exchange
 * del conto è dove il bot ESEGUE e non necessariamente dove GUARDA.
 */
export function mercatoDelBot(bot: Tradebot): MercatoDelBot {
  const mw = bot.marketWatcher;

  /* Il caso normale, da quando la rotta manda anche `marketWatcher`: exchange e
     coppia osservata sono letti, non dedotti. Il nome dell'exchange può ancora
     mancare — una risposta vecchia in cache, una join a metà — e allora si
     nomina l'id invece di tacere, ma la CERTEZZA non cambia: quella dipende da
     `exchangeId`, che è una colonna del market watcher, non dalla relazione. */
  if (mw && Number.isFinite(mw.exchangeId) && mw.asset && mw.currency) {
    const nomeExchange = nomeDi(mw.exchange?.name, mw.exchangeId);
    return {
      exchangeId: CERTO(
        mw.exchangeId,
        `${nomeExchange}, dal market watcher #${mw.id ?? bot.marketWatcherId}: è la sorgente delle candele su cui il bot decide.`,
      ),
      asset: CERTO(mw.asset, `coppia osservata dal market watcher #${mw.id ?? bot.marketWatcherId}.`),
      currency: CERTO(
        mw.currency,
        `coppia osservata dal market watcher #${mw.id ?? bot.marketWatcherId}.`,
      ),
      coppiaScambiataDiversa: notaCoppiaDiversa(bot, mw.asset, mw.currency, nomeExchange),
    };
  }

  if (bot.account && Number.isFinite(bot.account.exchangeId)) {
    const nome = nomeDi(bot.account.exchange?.name, bot.account.exchangeId);
    const spiega = `il market watcher #${bot.marketWatcherId} non è arrivato con la riga: qui c'è l'exchange del conto (${nome}), dove il bot ESEGUE. Se guarda le candele di un altro exchange, questo periodo misura un mercato diverso — correggilo con il selettore qui accanto.`;
    return {
      exchangeId: { valore: bot.account.exchangeId, certezza: 'ripiego', fonte: spiega },
      asset: {
        valore: bot.asset,
        certezza: 'ripiego',
        fonte:
          'coppia SCAMBIATA dal bot, non quella osservata: il market watcher non è arrivato. Scegli il market watcher qui accanto per avere quella giusta.',
      },
      currency: {
        valore: bot.currency,
        certezza: 'ripiego',
        fonte:
          'coppia SCAMBIATA dal bot, non quella osservata: il market watcher non è arrivato. Scegli il market watcher qui accanto per avere quella giusta.',
      },
      coppiaScambiataDiversa: null,
    };
  }

  const senza = `la riga non porta né il market watcher #${bot.marketWatcherId} né un conto: da qui non si può dedurre su quali candele il bot decide. Scegline uno con il selettore qui accanto — l'elenco è quello dei market watcher esistenti — oppure scrivi exchange e coppia a mano.`;
  return {
    exchangeId: { valore: null, certezza: 'ignoto', fonte: senza },
    asset: {
      valore: bot.asset,
      certezza: 'ripiego',
      fonte:
        'coppia SCAMBIATA dal bot: quella osservata non è deducibile. Scegli il market watcher qui accanto.',
    },
    currency: {
      valore: bot.currency,
      certezza: 'ripiego',
      fonte:
        'coppia SCAMBIATA dal bot: quella osservata non è deducibile. Scegli il market watcher qui accanto.',
    },
    coppiaScambiataDiversa: null,
  };
}

function nomeDi(nome: string | null | undefined, exchangeId: number): string {
  return nome && nome.trim() ? nome : `exchange #${exchangeId}`;
}

/**
 * La coppia scambiata è diversa da quella osservata: succede su catena.
 *
 * Non è un errore ed è la ragione per cui il modulo usa la coppia del market
 * watcher: il bot compra `BTCB` su BSC ma decide sulle candele `BTC/USDT` di un
 * exchange con book. Chi legge il risultato deve sapere quale delle due è stata
 * misurata, altrimenti confronta due mercati credendo di guardarne uno.
 */
function notaCoppiaDiversa(
  bot: Tradebot,
  asset: string,
  currency: string,
  nomeExchange: string,
): string | null {
  const diverso =
    asset.toUpperCase() !== bot.asset.toUpperCase() ||
    currency.toUpperCase() !== bot.currency.toUpperCase();
  if (!diverso) return null;
  return `Il bot scambia ${bot.asset}/${bot.currency}, ma decide sulle candele ${asset}/${currency} di ${nomeExchange}: su catena il token che si compra e il mercato che si guarda non hanno lo stesso nome. Il backtest misura quelle candele, che sono le stesse su cui il bot ha preso ogni decisione.`;
}

/* --------------------------------------------------------------------------
 * Il rimedio: quando la riga non lo dice, lo si sceglie
 * ------------------------------------------------------------------------ */

export interface SceltaMercato {
  exchangeId: number;
  asset: string;
  currency: string;
  /** Come è stata fatta la scelta: va scritto accanto al valore, non nascosto. */
  come: string;
}

/** Dal market watcher indicato da chi guarda. L'elenco è `GET /tradebots/market-watchers`. */
export function sceltaDaMarketWatcher(mw: MarketWatcher): SceltaMercato {
  const nome = nomeDi(mw.exchange?.name, mw.exchangeId);
  return {
    exchangeId: mw.exchangeId,
    asset: mw.asset,
    currency: mw.currency,
    come: `scelto da te: market watcher #${mw.id} — ${mw.asset}/${mw.currency} su ${nome}. Non viene dalla riga del bot.`,
  };
}

/** Exchange e coppia scritti a mano: l'ultima uscita, quando l'elenco non arriva. */
export function sceltaAMano(
  exchangeId: number,
  asset: string,
  currency: string,
  nomeExchange?: string | null,
): SceltaMercato {
  return {
    exchangeId,
    asset: asset.trim().toUpperCase(),
    currency: currency.trim().toUpperCase(),
    come: `scritto da te: ${asset.trim().toUpperCase()}/${currency.trim().toUpperCase()} su ${nomeDi(nomeExchange, exchangeId)}. Non viene dalla riga del bot: se l'exchange o la coppia sono sbagliati, il risultato descrive un altro mercato senza dare errore.`,
  };
}

/**
 * Il mercato scelto a mano, al posto di quello dedotto.
 *
 * La certezza diventa `scelto` e non `certo`: è vero che il valore c'è, ma
 * l'ha messo chi guarda e non la riga del bot, e la differenza resta accanto al
 * risultato finché quel risultato è a schermo.
 */
export function mercatoScelto(bot: Tradebot, s: SceltaMercato): MercatoDelBot {
  const dichiarato = <T,>(valore: T): Dichiarato<T> => ({
    valore,
    certezza: 'scelto',
    fonte: s.come,
  });
  return {
    exchangeId: dichiarato(s.exchangeId),
    asset: dichiarato(s.asset),
    currency: dichiarato(s.currency),
    coppiaScambiataDiversa: notaCoppiaDiversa(bot, s.asset, s.currency, `exchange #${s.exchangeId}`),
  };
}

/* ==========================================================================
 * I costi, coerenti con la sede
 * ======================================================================== */

const VENUE_DA_DEX: Record<string, string> = {
  ZEROX: '0x',
  ONEINCH: '1inch',
  VELORA: 'velora',
};

/** Gli exchange con book di cui `costi.ts` porta una misura. */
const VENUE_DA_EXCHANGE: { cerca: RegExp; id: string }[] = [
  { cerca: /binance/i, id: 'binance' },
  { cerca: /hyperliquid/i, id: 'hyperliquid' },
];

export interface VenueDelBot {
  venue: Dichiarato<Venue>;
  /**
   * Che cosa NON è stato usato come costo, e perché. Sta qui perché il campo
   * `swapSlippage` del bot è la prima cosa che verrebbe in mente di copiare, ed
   * è la cosa sbagliata da copiare.
   */
  notaTolleranza: string | null;
}

export function venueDelBot(bot: Tradebot): VenueDelBot {
  const trova = (id: string) => VENUE.find((v) => v.id === id) ?? null;

  if (bot.walletId != null) {
    const etichetta = bot.dex ? (ETICHETTE_DEX[bot.dex] ?? bot.dex) : null;
    const v = bot.dex ? trova(VENUE_DA_DEX[bot.dex]) : null;
    const notaTolleranza =
      bot.swapSlippage != null
        ? `Lo slippage massimo del bot (${num(bot.swapSlippage, 2)}%) NON è finito nei costi: è la tolleranza oltre la quale lo swap viene rifiutato, non il prezzo pagato. Il costo qui è quello misurato sulle transazioni reali.`
        : null;

    if (v) {
      return {
        venue: CERTO(v, `il bot opera su catena via ${etichetta}: ${v.nota}`),
        notaTolleranza,
      };
    }
    return {
      venue: {
        valore: null,
        certezza: 'ignoto',
        fonte: `il bot opera su catena${etichetta ? ` via ${etichetta}` : ''}, ma per questo venue non esiste una misura di costo in archivio. Non se ne inventa una: mettili a mano, o lancia senza costi sapendo che il risultato non è ottenibile.`,
      },
      notaTolleranza,
    };
  }

  const nomeExchange = bot.account?.exchange?.name ?? bot.account?.exchange?.slug ?? null;
  if (nomeExchange) {
    const trovato = VENUE_DA_EXCHANGE.find((r) => r.cerca.test(nomeExchange));
    const v = trovato ? trova(trovato.id) : null;
    if (v) {
      return { venue: CERTO(v, `il bot opera su ${nomeExchange}: ${v.nota}`), notaTolleranza: null };
    }
    return {
      venue: {
        valore: null,
        certezza: 'ignoto',
        fonte: `di ${nomeExchange} non esiste una misura di costo in archivio: le uniche misurate sono ${VENUE.filter((x) => x.id !== 'zero')
          .map((x) => x.nome)
          .join(', ')}. Mettili a mano invece di lasciarli a zero senza saperlo.`,
      },
      notaTolleranza: null,
    };
  }

  return {
    venue: {
      valore: null,
      certezza: 'ignoto',
      fonte:
        'la riga non dice su quale sede il bot opera: senza sede non c’è un costo di esecuzione da applicare.',
    },
    notaTolleranza: null,
  };
}

export function costiDaVenue(v: Venue): CostiEsecuzione {
  return { feeMode: v.feeMode, makerFee: v.makerFee, takerFee: v.takerFee, slippage: v.slippage };
}

/** Nessun costo: si usa solo quando la sede non è deducibile, e con l'avviso. */
export const COSTI_A_ZERO: CostiEsecuzione = {
  feeMode: 'TAKER',
  makerFee: 0,
  takerFee: 0,
  slippage: 0,
};

/* ==========================================================================
 * Il capitale d'avvio
 * ======================================================================== */

export interface CapitaleAvvio {
  assetBalance: Dichiarato<number>;
  currencyBalance: Dichiarato<number>;
}

/**
 * Con quanto far partire la prova.
 *
 * `initialAsset` e `initialCurrency` sono ciò con cui il trader del bot è stato
 * costruito davvero (`tradebot.robot.ts`), quindi quando ci sono sono il valore
 * esatto. Quando mancano si dichiara il ripiego: su una strategia long/flat la
 * scala non cambia il rendimento percentuale, ma cambia ogni cifra assoluta —
 * e partire con dell'asset in mano, invece che in valuta, cambia anche il primo
 * segnale utile.
 */
export function capitaleAvvioDelBot(bot: Tradebot): CapitaleAvvio {
  return {
    assetBalance:
      bot.initialAsset != null
        ? CERTO(bot.initialAsset, `saldo in ${bot.asset} con cui il bot è partito.`)
        : {
            valore: 0,
            certezza: 'ripiego',
            fonte: `il bot non ha registrato il saldo d’avvio in ${bot.asset}: la prova parte fuori mercato, cioè da zero asset.`,
          },
    currencyBalance:
      bot.initialCurrency != null
        ? CERTO(bot.initialCurrency, `capitale in ${bot.currency} con cui il bot è partito.`)
        : {
            valore: 1000,
            certezza: 'ripiego',
            fonte: `il bot non ha registrato il capitale d’avvio in ${bot.currency}: 1.000 è la convenzione del modulo. Il rendimento percentuale non cambia con la scala; ogni cifra assoluta sì.`,
          },
  };
}

/* ==========================================================================
 * Il periodo: proposto, e sempre modificabile
 * ======================================================================== */

export interface Copertura {
  /** Prima candela in magazzino per questa coppia, o `null` se non si sa. */
  prima: string | null;
  ultima: string | null;
}

export const SENZA_COPERTURA: Copertura = { prima: null, ultima: null };

/** Min e max delle candele che il magazzino ha per questa coppia. */
export function coperturaCandele(
  ranges: RangeDatiStorici[] | null | undefined,
  exchangeId: number | null,
  asset: string,
  currency: string,
): Copertura {
  if (!ranges || exchangeId == null) return SENZA_COPERTURA;
  const suoi = ranges.filter(
    (r) =>
      r.exchangeId === exchangeId &&
      r.asset.toUpperCase() === asset.toUpperCase() &&
      r.currency.toUpperCase() === currency.toUpperCase(),
  );
  if (suoi.length === 0) return SENZA_COPERTURA;
  return {
    prima: suoi.reduce((min, r) => (r.begin < min ? r.begin : min), suoi[0].begin),
    ultima: suoi.reduce((max, r) => (r.end > max ? r.end : max), suoi[0].end),
  };
}

export interface PeriodoProposto {
  dal: string;
  al: string;
  /** Da dove viene la proposta. Il periodo resta libero: questa è la partenza. */
  fonte: string;
}

const FORMATO_GIORNO = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export function giorno(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function dataDi(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function menoGiorni(d: Date, g: number): Date {
  return new Date(d.getTime() - g * 86400000);
}

/**
 * Il periodo di partenza: quello del bot, arretrato di quanto serve al warmup.
 *
 * L'arretramento non è un dettaglio. Il motore consuma il warmup DENTRO la
 * finestra richiesta: chiedere «dal giorno in cui il bot è partito» a un bot con
 * 2.700 candele da un'ora vuol dire che i primi 112 giorni della finestra non
 * misurano niente, e su un bot acceso da una settimana la finestra misurata
 * sarebbe vuota — con un risultato completo, plausibile e senza operazioni.
 * Arretrando l'inizio, il periodo MISURATO coincide con la vita del bot, che è
 * la domanda che si sta facendo.
 */
export function periodoProposto(bot: Tradebot, copertura: Copertura): PeriodoProposto {
  const oggi = new Date();
  const fineDati = dataDi(copertura.ultima);
  const al = fineDati && fineDati < oggi ? fineDati : oggi;

  const misura = dataDi(bot.measuredSince);
  const avvio = dataDi(bot.startDate) ?? dataDi(bot.createdAt);
  const inizio = misura ?? avvio;

  const giorniW = Math.ceil(giorniDiWarmup(bot.warmupPeriod, bot.candleSize) ?? 0);
  const primaDati = dataDi(copertura.prima);

  /* Nessuna data sulla riga: non si finge di sapere da quando il bot misura.
     Due anni è una finestra dichiarata come convenzione, non come deduzione. */
  if (!inizio) {
    const grezzo = menoGiorni(al, 730);
    const dal = primaDati && grezzo < primaDati ? primaDati : grezzo;
    return {
      dal: giorno(dal),
      al: giorno(al),
      fonte:
        'la riga non porta né la data di misura né quella di avvio: questi due anni sono una convenzione del modulo, non un periodo dedotto dal bot. Cambialo.',
    };
  }

  const grezzo = menoGiorni(inizio, giorniW);
  const tagliato = primaDati != null && grezzo < primaDati;
  const dal = tagliato ? (primaDati as Date) : grezzo;

  const daDove = misura
    ? `dal ${FORMATO_GIORNO.format(inizio)}, quando il bot ha fissato la misura`
    : `dall’avvio del bot (${FORMATO_GIORNO.format(inizio)}): la riga non porta la data di misura, che è più tarda di tutto il warmup`;

  const arretra =
    giorniW > 0
      ? `, arretrato di ${num(giorniW)} giorni per il warmup (${num(bot.warmupPeriod)} candele da ${num(bot.candleSize)} min), così il periodo davvero misurato coincide con la vita del bot`
      : '';

  const taglio = tagliato
    ? ` L’arretramento è stato tagliato alla prima candela in magazzino (${giorno(primaDati as Date)}): prima di quella data non ci sono dati, e il warmup resta incompleto.`
    : '';

  const fine = fineDati && fineDati < oggi ? ` La fine è l’ultima candela in magazzino.` : '';

  return { dal: giorno(dal), al: giorno(al), fonte: `${daDove}${arretra}.${taglio}${fine}` };
}

/* ==========================================================================
 * Perché da questo bot non si può rifare la misura
 * ======================================================================== */

/**
 * `null` se la prova si può lanciare.
 *
 * Senza `strategyParams` il backtest partirebbe con i predefiniti del motore:
 * stessa strategia, altri parametri, altro risultato — e nessun errore. È lo
 * stesso motivo per cui `creazione.ts` non lascia creare un bot da un backtest
 * senza parametri, guardato dall'altro lato.
 */
export function motivoNonRieseguibile(bot: Tradebot): string | null {
  if (bot.strategyParams == null || Object.keys(bot.strategyParams).length === 0) {
    return `Il bot #${bot.id} non porta con sé i parametri della strategia: la riga ha strategyParams vuoto. Un backtest lanciato così userebbe i predefiniti del motore — stessa strategia, altri numeri — e il risultato non direbbe niente su questo bot.`;
  }
  if (!bot.strategyName) return `Il bot #${bot.id} non dichiara quale strategia esegue.`;
  if (!(bot.candleSize > 0)) {
    return `Il bot #${bot.id} non ha una dimensione di candela valida: senza, il motore non sa su cosa misurare.`;
  }
  return null;
}
